import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Breadcrumbs } from '../components/shared/Breadcrumbs';

export default function AboutPage() {
  return (
    <>
      <Helmet>
        <title>About AzureCalc.uk | Independent Azure Cost Calculator UK</title>
        <meta name="description" content="AzureCalc.uk is a free, independent Azure cost calculator for UK South in GBP. Built by a solo developer using the Azure Retail Prices API." />
        <link rel="canonical" href="https://www.azure-calc.co.uk/about" />
        <meta property="og:title" content="About AzureCalc.uk" />
        <meta property="og:description" content="Free, independent Azure cost calculator for UK South in GBP. Built by a solo developer — not affiliated with Microsoft." />
        <meta property="og:url" content="https://www.azure-calc.co.uk/about" />
        <meta property="og:image" content="https://www.azure-calc.co.uk/og/azure-calc-og.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'About AzureCalc.uk',
          url: 'https://www.azure-calc.co.uk/about',
          description: 'AzureCalc.uk is a free, independent Azure cost estimator for UK South, built by a solo developer.',
          publisher: {
            '@type': 'Person',
            name: 'AzureCalc.uk',
            url: 'https://www.azure-calc.co.uk',
          },
        })}</script>
      </Helmet>

      <div className="px-6 py-6 w-full max-w-3xl mx-auto">
        <Breadcrumbs />

        <h1 className="text-3xl font-extrabold tracking-tight mb-4" style={{ color: 'var(--text-primary)' }}>
          About AzureCalc.uk
        </h1>

        <p style={{ color: 'var(--text-tertiary)' }} className="text-[15px] leading-relaxed mb-6">
          I built AzureCalc.uk because I kept hitting the same wall: the official Azure Pricing
          Calculator is fine for simple scenarios, but the moment you're trying to model something
          like a Log Analytics workspace with Sentinel enabled and mixed retention policies, it
          becomes genuinely painful. You can't see the actual GBP rates without navigating across
          multiple pages, and the commitment tier maths is left as an exercise for the reader.
          I wanted a tool that did that work upfront, showed the formula, and used real prices.
        </p>

        <h2 className="text-[17px] font-bold mb-3 mt-8" style={{ color: 'var(--text-primary)' }}>Where the prices come from</h2>
        <p style={{ color: 'var(--text-tertiary)' }} className="text-[15px] leading-relaxed mb-6">
          Every price on this site is fetched nightly from the{' '}
          <strong style={{ color: 'var(--text-secondary)' }}>Microsoft Azure Retail Prices API</strong> — the
          same public API that powers the official Azure pricing pages. I filter to
          <code className="text-[#0078d4] mx-1 px-1 rounded" style={{ backgroundColor: 'var(--surface-container)' }}>uksouth</code> and
          <code className="text-[#0078d4] mx-1 px-1 rounded" style={{ backgroundColor: 'var(--surface-container)' }}>GBP</code> so
          you see native pound prices, not converted USD. The cache refreshes at 02:00 UTC;
          a timestamp is shown on every calculator.{' '}
          <Link to="/methodology" className="text-[#0078d4] hover:underline">
            Full methodology →
          </Link>
        </p>

        <h2 className="text-[17px] font-bold mb-3 mt-8" style={{ color: 'var(--text-primary)' }}>Who built it</h2>
        <p style={{ color: 'var(--text-tertiary)' }} className="text-[15px] leading-relaxed mb-6">
          I'm a solo developer who works with Azure regularly on UK production workloads. The
          calculation formulas are derived from official Microsoft documentation and verified
          against real invoice line items. I also built an{' '}
          <Link to="/" className="text-[#0078d4] hover:underline">AI cost assistant</Link>
          {' '}that understands natural-language cost questions and can query the calculators
          directly. AzureCalc.uk is not affiliated with, endorsed by, or sponsored by Microsoft
          Corporation.
        </p>

        <p className="text-[13px] mb-8" style={{ color: 'var(--text-tertiary)' }}>
          — <a href="mailto:gravitycontextdev@gmail.com" className="hover:opacity-70">gravitycontextdev@gmail.com</a>
        </p>

        {/* Expertise & Credentials Section */}
        <h2 className="text-[17px] font-bold mb-3 mt-8" style={{ color: 'var(--text-primary)' }}>Expertise & Real-World Experience</h2>
        <p style={{ color: 'var(--text-tertiary)' }} className="text-[15px] leading-relaxed mb-6">
          AzureCalc.uk is built on practical experience with Azure production systems, not theory.
          The formulas and edge cases documented here come from:
        </p>
        <ul style={{ color: 'var(--text-tertiary)' }} className="list-disc pl-5 mb-6 space-y-2">
          <li><strong>5+ years of Azure production workload management</strong> across compute, storage, networking, and monitoring services</li>
          <li><strong>Invoice reconciliation work</strong> at scale — matching Microsoft Retail Prices API data against actual billing for enterprise and SMB customers</li>
          <li><strong>Cost surprises turned into documentation</strong> — every guide here emerged from a real cost discovery or optimization project</li>
          <li><strong>Official documentation cross-referencing</strong> — every calculation is traced back to Microsoft documentation, not guesswork</li>
          <li><strong>Community feedback</strong> — calculator improvements are driven by engineer questions and cost reconciliation edge cases</li>
        </ul>

        {/* Methodology & Verification Section */}
        <h2 className="text-[17px] font-bold mb-3 mt-8" style={{ color: 'var(--text-primary)' }}>How Accuracy Is Verified</h2>
        <p style={{ color: 'var(--text-tertiary)' }} className="text-[15px] leading-relaxed mb-6">
          Prices are not guessed. Every number on AzureCalc.uk is sourced from the{' '}
          <a
            href="https://learn.microsoft.com/en-us/rest/api/cost-management/retail-prices/azure-retail-prices"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#0078d4] hover:underline"
          >
            Microsoft Azure Retail Prices API
          </a>
          {' '}and regularly verified against:
        </p>
        <ul style={{ color: 'var(--text-tertiary)' }} className="list-disc pl-5 mb-6 space-y-2">
          <li><strong>Quarterly invoice reconciliation:</strong> Real customer bills are matched against estimates. Variance gaps are documented and investigated.</li>
          <li><strong>Spot checks against official pages:</strong> I regularly verify prices against Microsoft's official pricing pages for the top 5 services.</li>
          <li><strong>Peer review:</strong> Formulas are cross-checked against published Azure architecture guidance and community cost models.</li>
          <li><strong>Edge case tracking:</strong> User reports of unexpected costs are investigated and added to the <Link to="/guides/azure-billing-traps-uk" className="text-[#0078d4] hover:underline">billing traps guide</Link> if confirmed.</li>
        </ul>

        {/* Transparency Section */}
        <h2 className="text-[17px] font-bold mb-3 mt-8" style={{ color: 'var(--text-primary)' }}>Transparency & No Hidden Agendas</h2>
        <p style={{ color: 'var(--text-tertiary)' }} className="text-[15px] leading-relaxed mb-6">
          AzureCalc.uk has no hidden affiliations or conflicts of interest:
        </p>
        <ul style={{ color: 'var(--text-tertiary)' }} className="list-disc pl-5 mb-6 space-y-2">
          <li><strong>No vendor relationship with Microsoft:</strong> This site does not receive funding, data, or support from Microsoft. All data comes from the publicly available Azure Retail Prices API.</li>
          <li><strong>No affiliate-driven business model:</strong> While affiliate links to certification courses appear on guides, the recommendations are based on content relevance, not commission rates.</li>
          <li><strong>Open feedback loop:</strong> Errors are corrected within 24 hours. Every email gets a response.</li>
          <li><strong>Pricing accuracy is the core metric:</strong> If a price is wrong, the site loses utility. Accuracy is the only metric that matters.</li>
        </ul>

        {/* Community Engagement */}
        <h2 className="text-[17px] font-bold mb-3 mt-8" style={{ color: 'var(--text-primary)' }}>Community Engagement</h2>
        <p style={{ color: 'var(--text-tertiary)' }} className="text-[15px] leading-relaxed mb-6">
          AzureCalc.uk is shaped by real engineer questions and feedback. If multiple people report the same issue or ask for the same feature, the site evolves. Recent updates driven by community requests:
        </p>
        <ul style={{ color: 'var(--text-tertiary)' }} className="list-disc pl-5 mb-6 space-y-2">
          <li>Added a dedicated guide on <Link to="/guides/log-analytics-search-job-vs-restore" className="text-[#0078d4] hover:underline">Log Analytics Search Job vs Restore costs</Link> after multiple engineers asked about the 2TB minimum</li>
          <li>Expanded <Link to="/guides/azure-networking-costs-uk" className="text-[#0078d4] hover:underline">Networking section</Link> after discovering NAT Gateway always-on charges were consistently underestimated</li>
          <li>Added <Link to="/guides/invoice-reconciliation" className="text-[#0078d4] hover:underline">Invoice Reconciliation guide</Link> as a response to "why doesn't my estimate match my bill?"</li>
        </ul>

        <h2 className="text-[17px] font-bold mb-3 mt-8" style={{ color: 'var(--text-primary)' }}>Accuracy and limitations</h2>
        <p style={{ color: 'var(--text-tertiary)' }} className="text-[15px] leading-relaxed mb-6">
          Estimates are for guidance only. Actual costs depend on your Microsoft agreement,
          reserved instance pricing, enterprise discounts, and any commitment plans you hold.
          Always verify through the{' '}
          <a
            href="https://azure.microsoft.com/pricing/calculator"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#0078d4] hover:underline"
          >
            official Azure Pricing Calculator
          </a>{' '}
          before making purchasing decisions. See the full{' '}
          <Link to="/disclaimer" className="text-[#0078d4] hover:underline">disclaimer</Link> for
          details.
        </p>

        <h2 className="text-[17px] font-bold mb-3 mt-8" style={{ color: 'var(--text-primary)' }}>Why this site exists</h2>
        <p style={{ color: 'var(--text-tertiary)' }} className="text-[15px] leading-relaxed mb-6">
          Azure pricing is powerful but fractured. Microsoft publishes meters at scale, but helping
          engineers make sense of _combinations_ of meters in realistic scenarios is left to the user.
          Most cost estimation happens either through the official calculator (which is designed for
          simplicity, not depth) or through internal spreadsheets (which are version-fragmented and
          error-prone). When procurement teams ask "what will this architecture cost?", engineers
          either guess, call sales (getting opaque pricing), or spend hours cross-referencing docs.
        </p>
        <p style={{ color: 'var(--text-tertiary)' }} className="text-[15px] leading-relaxed mb-6">
          AzureCalc.uk exists to bridge that gap. It takes the Azure Retail Prices API (which is
          reliable but raw) and wraps it with service-specific expertise so that answers come with
          caveats, examples, and next steps rather than just a number. The theory is: a calculator
          that explains itself is more useful than a calculator that just outputs a total.
        </p>

        <h2 className="text-[17px] font-bold mb-3 mt-8" style={{ color: 'var(--text-primary)' }}>Design philosophy</h2>
        <p style={{ color: 'var(--text-tertiary)' }} className="text-[15px] leading-relaxed mb-6">
          AzureCalc.uk prioritizes transparency over polish. Every estimate includes:
        </p>
        <ul style={{ color: 'var(--text-tertiary)' }} className="list-disc pl-5 mb-6 space-y-2">
          <li><strong>A formula or breakdown</strong> so you understand what's being calculated</li>
          <li><strong>Caveats and limitations</strong> so you know when the estimate might be wrong</li>
          <li><strong>Related guides</strong> so you can drill deeper into the pricing logic without having to search elsewhere</li>
          <li><strong>Links to provenance</strong> so you know where the number came from</li>
          <li><strong>Timestamp</strong> so you know the estimate is current (prices change monthly)</li>
        </ul>
        <p style={{ color: 'var(--text-tertiary)' }} className="text-[15px] leading-relaxed mb-6">
          It's not designed to be a pretty marketing site or a one-click quote tool. It's designed
          to be the reference you keep open at 4 PM on Friday when you're trying to figure out whether
          a customer's cost estimate is plausible before you present it Monday morning.
        </p>

        <h2 className="text-[17px] font-bold mb-3 mt-8" style={{ color: 'var(--text-primary)' }}>Data freshness and updates</h2>
        <p style={{ color: 'var(--text-tertiary)' }} className="text-[15px] leading-relaxed mb-6">
          Prices refresh nightly at 02:00 UTC. Every calculator displays a timestamp showing when
          the cached prices were last updated. If prices haven't refreshed within 24 hours, a
          warning appears so you know to defer cost-sensitive decisions or use the official calculator
          to double-check. You can also check the {' '}
          <Link to="/history" className="text-[#0078d4] hover:underline">price history page</Link>{' '}
          to see recent price movements across services.
        </p>

        <h2 className="text-[17px] font-bold mb-3 mt-8" style={{ color: 'var(--text-primary)' }}>Get in touch</h2>
        <p style={{ color: 'var(--text-tertiary)' }} className="text-[15px] leading-relaxed mb-6">
          Found an error? Have a feature request? Spotted a price that looks wrong? Reach out at{' '}
          <a href="mailto:gravitycontextdev@gmail.com" className="text-[#0078d4] hover:underline">
            gravitycontextdev@gmail.com
          </a>. I read every email and iterate based on feedback. The site is independent, which
          means there's no roadmap except the one users tell me about.
        </p>

        <h2 className="text-[17px] font-bold mb-3 mt-8" style={{ color: 'var(--text-primary)' }}>Calculators available</h2>
        <ul style={{ color: 'var(--text-tertiary)' }} className="text-[15px] leading-relaxed space-y-2 mb-8">
          {[
            { label: 'Azure Blob Storage', path: '/blob-storage' },
            { label: 'Azure Logic Apps', path: '/logic-apps' },
            { label: 'Azure Functions', path: '/functions' },
            { label: 'Azure App Service', path: '/app-service' },
            { label: 'Azure Bandwidth & Egress', path: '/bandwidth' },
            { label: 'Azure Log Analytics & Sentinel', path: '/log-analytics' },
            { label: 'Azure Virtual Machines', path: '/virtual-machines' },
          ].map(({ label, path }) => (
            <li key={path}>
              <Link to={path} className="text-[#0078d4] hover:underline">{label}</Link>
            </li>
          ))}
        </ul>

        <div className="pt-6 text-[13px]" style={{ borderTop: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}>
          Pricing data sourced from the Microsoft Azure Retail Prices API. Not affiliated with Microsoft.
          All estimates are approximations — see the <Link to="/disclaimer" className="hover:opacity-70">disclaimer</Link>.
        </div>
      </div>
    </>
  );
}
